import { useState, useEffect } from 'react';
import { getReaderProfileOnboardingConfig } from '../../../services/configuration-service';
import { readingPracticeRequestsService } from '../../../services/request-service';

const schoolLevels = [
  { value: 'ELEMENTARY', label: 'Elementary' },
  { value: 'MIDDLE', label: 'Middle' },
  { value: 'HIGH', label: 'High' }
];

const contentStyles = [
  {
    "name": "SIMPLE_SENTENCES",
    "description": "Simple sentences, repetitive structures"
  },
  {
    "name": "SHORT_PARAGRAPHS",
    "description": "Short paragraphs, simple narrative"
  },
  {
    "name": "SLIGHTLY_COMPLEX",
    "description": "Slightly more complex sentences"
  },
  {
    "name": "DIALOGUE_DESCRIPTIVE",
    "description": "Introduce dialogue, more descriptive language"
  },
  {
    "name": "COMPLEX_NARRATIVES",
    "description": "More complex narratives and descriptions"
  },
  {
    "name": "METAPHORS_VARIED",
    "description": "Include metaphors, varied sentence structures"
  },
  {
    "name": "CRITICAL_THINKING",
    "description": "Encourage critical thinking, sophisticated language"
  },
  {
    "name": "ADVANCED_STRUCTURES",
    "description": "Advanced sentence structures, in-depth analysis"
  }
];

const getDefaultOverridesForSchoolLevel = (schoolLevel) => {
  switch (schoolLevel) {
    case 'ELEMENTARY':
      return {
        totalWords: 350,
        decodeWords: 5,
        questionCount: 4,
        optionCount: 4,
        paragraphContentStyle: 'SHORT_PARAGRAPHS',
      };
    case 'HIGH':
      return {
        totalWords: 1000,
        decodeWords: 10,
        questionCount: 6,
        optionCount: 4,
        paragraphContentStyle: 'SLIGHTLY_COMPLEX',
      };
    case 'MIDDLE':
    default:
      return {
        totalWords: 900,
        decodeWords: 8,
        questionCount: 5,
        optionCount: 4,
        paragraphContentStyle: 'SLIGHTLY_COMPLEX',
      };
  }
};

export const useCreatePracticeCollectionForm = () => {
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);

  const [formValues, setFormValues] = useState({
    schoolLevel: 'MIDDLE',
    sourceMaterial: '',
    type: 'NON_FICTION',
    numberOfFiction: 3,
    numberOfNonFiction: 2,
    totalWords: 900,
    decodeWords: 8,
    questionCount: 5,
    optionCount: 4,
    category: '',
    paragraphContentStyle: 'SLIGHTLY_COMPLEX',
    userNote: '',
    isPathway: false,
  });

  // Fetch configuration on component mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const configData = await getReaderProfileOnboardingConfig();
        setConfig({ ...configData, schoolLevels, contentStyles });
      } catch (err) {
        setError(err.message);
        console.error('Failed to load configuration:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormValues(prev => {
      const val = type === 'checkbox' ? checked : value;
      const updated = {
        ...prev,
        [name]: val,
      };

      if (name === 'isPathway') {
        if (val) {
          // Locked values for pathway
          return {
            ...updated,
            type: 'PATHWAY',
            schoolLevel: 'HIGH',
            totalWords: 1000,
            decodeWords: 10,
            questionCount: 6,
            optionCount: 4,
            paragraphContentStyle: 'SLIGHTLY_COMPLEX',
          };
        } else {
          // Reset to default normal collection values
          return {
            ...updated,
            type: 'NON_FICTION',
            schoolLevel: 'MIDDLE',
            totalWords: 900,
            decodeWords: 8,
            questionCount: 5,
            optionCount: 4,
            paragraphContentStyle: 'SLIGHTLY_COMPLEX',
          };
        }
      }

      if (name === 'schoolLevel' && !prev.isPathway) {
        const defaults = getDefaultOverridesForSchoolLevel(value);
        return {
          ...updated,
          ...defaults,
        };
      }
      return updated;
    });
  };

  const resetForm = () => {
    setFormValues({
      schoolLevel: 'MIDDLE',
      sourceMaterial: '',
      type: 'NON_FICTION',
      numberOfFiction: 3,
      numberOfNonFiction: 2,
      totalWords: 900,
      decodeWords: 8,
      questionCount: 5,
      optionCount: 4,
      category: '',
      paragraphContentStyle: 'SLIGHTLY_COMPLEX',
      userNote: '',
      isPathway: false,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validation
    if (!formValues.schoolLevel || !formValues.sourceMaterial) {
      setError('Please fill in all required fields');
      return null;
    }

    const requestData = {
      sourceMaterial: formValues.sourceMaterial,
      schoolLevel: formValues.schoolLevel,
      type: formValues.type,
      numberOfFiction: parseInt(formValues.numberOfFiction, 10),
      numberOfNonFiction: parseInt(formValues.numberOfNonFiction, 10),
      totalWords: formValues.totalWords ? parseInt(formValues.totalWords, 10) : null,
      decodeWords: formValues.decodeWords ? parseInt(formValues.decodeWords, 10) : null,
      questionCount: formValues.questionCount ? parseInt(formValues.questionCount, 10) : null,
      optionCount: formValues.optionCount ? parseInt(formValues.optionCount, 10) : null,
      categoryId: formValues.category || null,
      paragraphContentStyle: formValues.paragraphContentStyle || null,
      userNote: formValues.userNote || null,
    };

    setCreating(true);
    setError(null);

    try {
      const result = await readingPracticeRequestsService.submitPracticeCollectionRequest(requestData);
      resetForm();
      return result;
    } catch (err) {
      console.error('Error in practice collection generation:', err);
      setError(err.message);
      return null;
    } finally {
      setCreating(false);
    }
  };

  return {
    creating,
    formValues,
    handleChange,
    handleSubmit,
    loading,
    error,
    config,
  };
};
